<template>
  <Flex
    direction="column"
    fullWidth
  >
    <Flex
      align="center"
      justify="between"
    >
      <Flex
        direction="column"
        :gap=".25"
      >
        <BaseHeading size="l">
          {{ $t('register.salonized_pay.dashboard.business_details.heading') }}
        </BaseHeading>
        <BaseText color="secondary">
          {{ $t('register.salonized_pay.dashboard.business_details.subheading') }}
        </BaseText>
      </Flex>
      <BaseButton
        v-if="hasFeatureFlag('admin-salonized-pay')"
        color="inverted"
        icon="external_link"
        @click="$emit('generateOnboardingLink')"
        v-test="'sp-business-details-open-adyen'"
      >
        {{ $t('register.salonized_pay.dashboard.business_details.open_adyen') }}
      </BaseButton>
    </Flex>

    <BaseCard
      :heading="$t('register.salonized_pay.dashboard.business_details.business.heading')"
      v-test="'sp-business-details-business'"
    >
      <div :class="$style.row">
        <BaseText color="secondary">
          {{ $t('register.salonized_pay.dashboard.business_details.business.legal_name') }}
        </BaseText>
        <BaseText v-test="'sp-business-details-legal-name'">
          {{ company.name }}
        </BaseText>
      </div>
      <div :class="$style.row">
        <BaseText color="secondary">
          {{ $t('register.salonized_pay.dashboard.business_details.business.business_type') }}
        </BaseText>
        <BaseText v-test="'sp-business-details-business-type'">
          {{ businessTypeLabel }}
        </BaseText>
      </div>
      <div :class="[$style.row, $style.lastRow]">
        <BaseText color="secondary">
          {{ $t('register.salonized_pay.dashboard.business_details.business.verification') }}
        </BaseText>
        <BaseLabel
          v-if="isVerified"
          color="success"
          v-test="'sp-business-details-verified'"
        >
          {{ $t('register.salonized_pay.dashboard.business_details.business.verified') }}
        </BaseLabel>
      </div>
    </BaseCard>

    <BaseCard
      :heading="$t('register.salonized_pay.dashboard.business_details.bank_accounts.heading')"
      v-test="'sp-business-details-bank-accounts'"
    >
      <div :class="$style.rows">
        <div
          v-for="transferInstrument in transferInstruments"
          :key="transferInstrument.id"
          :class="$style.row"
          v-test="'sp-business-details-bank-account'"
        >
          <BaseText>
            •••• {{ transferInstrument.accountNumberLast4 }}
          </BaseText>
          <BaseLabel
            v-if="isVerified"
            color="success"
            v-test="'sp-business-details-bank-account-verified'"
          >
            {{ $t('register.salonized_pay.dashboard.business_details.business.verified') }}
          </BaseLabel>
        </div>
      </div>
      <BaseText
        color="secondary"
        v-test="'sp-business-details-bank-accounts-footer'"
      >
        {{ $t('register.salonized_pay.dashboard.business_details.bank_accounts.footer') }}
      </BaseText>
    </BaseCard>
  </Flex>
</template>

<script
  lang="ts"
  setup
>
import { useQuery } from '@vue/apollo-composable';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCompanyStore } from '@/stores/company';
import { useUserStore } from '@/stores/user';
import { GET_ADYEN_BUSINESS_DETAILS } from '../graphql';

defineEmits(['generateOnboardingLink']);

const { t } = useI18n();
const { company } = useCompanyStore();
const { hasFeatureFlag } = useUserStore();

const { result } = useQuery(GET_ADYEN_BUSINESS_DETAILS);

const adyenAppState = computed(() => result.value?.adyenAppState);

const isVerified = computed(
  () => adyenAppState.value?.verificationStatus === 'VALID',
);

const businessTypeLabels: Record<string, string> = {
  organization: t(
    'register.salonized_pay.dashboard.business_details.legal_entity_type.organization',
  ),
  individual: t(
    'register.salonized_pay.dashboard.business_details.legal_entity_type.individual',
  ),
  sole_proprietorship: t(
    'register.salonized_pay.dashboard.business_details.legal_entity_type.individual',
  ),
};

const businessTypeLabel = computed(() => {
  const legalEntityType = adyenAppState.value?.legalEntityType;
  if (!legalEntityType) return '';
  return businessTypeLabels[legalEntityType] ?? legalEntityType;
});

const transferInstruments = computed(
  () => adyenAppState.value?.transferInstruments ?? [],
);
</script>

<style
  lang="scss"
  module
>
.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $spacing 0;
  border-bottom: 1px solid var(--color-border);
}

.lastRow {
  border-bottom: none;
}

.rows {
  .row:last-child {
    border-bottom: none;
  }
}
</style>
